import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowLeft, Mail, LifeBuoy } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { identifier });
      setResult(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : t('forgot_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--color-bg)] flex items-center justify-center px-4 relative"
    >
      <div className="absolute inset-0 bg-sand overflow-hidden pointer-events-none"><div className="absolute -left-32 -top-32 w-[28rem] h-[28rem] rounded-full bg-[color:var(--color-primary)] opacity-10" /><div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-[color:var(--color-emerald)] opacity-10" /></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-[color:rgb(var(--surface-rgb)/0.92)] backdrop-blur-xl border border-white/10 p-8 rounded-3xl shadow-xl">
          <div className="text-center mb-8">
            <Link to="/">
              <span className="text-3xl font-bold tracking-tight text-[color:var(--color-primary)]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <span style={{ color: 'var(--color-logo-gold)' }}>Ô'</span>Delicieuse <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[color:var(--color-emerald)] align-middle ml-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Wholesale</span>
              </span>
            </Link>
          </div>

          {!result ? (
            <>
              <h1 className="text-3xl text-white font-light text-center mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('forgot_title')}
              </h1>
              <p className="text-zinc-400 text-sm text-center mb-8">{t('forgot_desc')}</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="input-label">{t('forgot_identifier')}</label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    className="w-full input-field"
                    placeholder="+32 xxx xx xx xx  /  email@exemple.com"
                    data-testid="forgot-phone-input"
                  />
                </div>
                {error && <p className="text-red-500 text-sm" data-testid="forgot-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black px-6 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors font-medium disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  <LifeBuoy className="w-5 h-5" />
                  {loading ? '...' : t('forgot_submit')}
                </button>
              </form>
            </>
          ) : result.method === 'email' ? (
            <div className="text-center" data-testid="forgot-success-email">
              <div className="w-16 h-16 bg-[color:rgb(var(--primary-rgb)/0.10)] border border-[color:rgb(var(--primary-rgb)/0.30)] flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[color:var(--color-primary)]" />
              </div>
              <h2 className="text-2xl text-white font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('forgot_email_title')}</h2>
              <p className="text-zinc-400 text-sm mb-4">{t('forgot_email_desc', { email: result.email_masked })}</p>
              <p className="text-zinc-500 text-xs">{t('forgot_email_hint')}</p>
            </div>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-16 h-16 bg-[color:rgb(37_211_102/0.10)] border border-[color:rgb(37_211_102/0.30)] flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="w-8 h-8 text-[#25D366]" />
              </div>
              <h2 className="text-2xl text-white font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {result.method === 'admin' ? t('forgot_admin_title') : t('forgot_none_title')}
              </h2>
              <p className="text-zinc-400 text-sm mb-6">
                {result.method === 'admin' ? (result.reason === 'email_failed' ? t('forgot_admin_desc_failed') : t('forgot_admin_desc')) : t('forgot_none_desc')}
              </p>
              <a href={result.whatsapp_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 hover:bg-[#20BD5A] transition-colors font-medium"
                data-testid="forgot-whatsapp-btn">
                <MessageCircle className="w-5 h-5" /> {t('forgot_whatsapp_btn')}
              </a>
              <p className="text-zinc-500 text-xs mt-6">{t('forgot_sent_delay')}</p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/connexion" className="inline-flex items-center gap-2 text-zinc-500 hover:text-[color:var(--color-primary)] text-sm transition-colors" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" />
              {t('forgot_back_login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
